import React, { useState, useEffect } from 'react';
import { Copy, CheckCircle, Code, Info, Plus, XCircle } from 'lucide-react';
import axios from 'axios';

const TrackingCodeManager = () => {
  const [copied, setCopied] = useState(false);
  const [currentSiteId, setCurrentSiteId] = useState(null);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customParams, setCustomParams] = useState([{ name: '', value: '' }]);
  
  // Fetch sites when component mounts
  useEffect(() => {
    fetchSites();
  }, []);
  
  const fetchSites = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get('/api/sites');
      setSites(response.data);
      
      // Set the current site to the first active site
      const activeSite = response.data.find(site => site.active);
      if (activeSite) {
        setCurrentSiteId(activeSite.id);
      } else if (response.data.length > 0) {
        setCurrentSiteId(response.data[0].id);
      }
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError('Failed to load your sites. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Generate the tracking code
  const generateTrackingCode = () => {
    if (!currentSiteId) return '';
    
    let code = `<script src="https://visitorstatus.com/tracker.js?site=${currentSiteId}`;
    
    // Add custom parameters
    customParams.forEach(param => {
      if (param.name && param.value) {
        code += `&${param.name}=${encodeURIComponent(param.value)}`;
      }
    });
    
    code += `"></script>`;
    return code;
  };
  
  const handleCopy = () => {
    navigator.clipboard.writeText(generateTrackingCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleAddParam = () => {
    setCustomParams([...customParams, { name: '', value: '' }]);
  };
  
  const handleParamChange = (index, field, value) => {
    const updatedParams = [...customParams];
    updatedParams[index][field] = value;
    setCustomParams(updatedParams);
  };
  
  const handleRemoveParam = (index) => {
    const updatedParams = [...customParams];
    updatedParams.splice(index, 1);
    setCustomParams(updatedParams);
  };
  
  // Find the current site
  const currentSite = sites.find(site => site.id === currentSiteId);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }
  
  // No sites state
  if (sites.length === 0) {
    return (
      <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
        <div className="flex">
          <Info className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-yellow-800">No Sites Available</h3>
            <div className="mt-2">
              <p className="text-yellow-700">
                You need to create a site before you can generate a tracking code.
              </p>
              <a 
                href="/sites" 
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create a Site
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Tracking Code</h1>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
          <button 
            className="absolute top-0 right-0 p-3" 
            onClick={() => setError(null)}
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Your Tracking Code</h2>
        
        {sites.length > 1 && (
          <div className="mb-6">
            <label htmlFor="site-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select Site
            </label>
            <select
              id="site-select"
              value={currentSiteId || ''}
              onChange={(e) => setCurrentSiteId(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name} ({site.domain})
                </option>
              ))}
            </select>
          </div>
        )}
        
        <p className="text-gray-600 mb-4">
          Add this code to your website to start tracking visitors for {currentSite?.name || 'your site'}.
        </p>
        
        <div className="relative bg-gray-50 p-4 rounded-md border border-gray-200 mb-6">
          <pre className="text-sm text-gray-800 overflow-x-auto">{generateTrackingCode()}</pre>
          <button 
            onClick={handleCopy} 
            className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700"
            title="Copy to clipboard"
          >
            {copied ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
          </button>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mb-6">
          <div className="flex">
            <Code className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">How to use</h3>
              <p className="text-sm text-blue-700 mt-1">
                Place this code in the <code className="bg-blue-100 px-1 py-0.5 rounded">&lt;head&gt;</code> section of every page you want to track.
              </p>
            </div>
          </div>
        </div>
        
        <h3 className="text-md font-medium text-gray-900 mb-2">Custom Parameters</h3>
        <p className="text-gray-600 mb-4">
          Add custom parameters to track additional information about your visitors:
        </p>
        
        <div className="space-y-3 mb-6">
          {customParams.map((param, index) => (
            <div key={index} className="flex space-x-2">
              <input
                type="text"
                value={param.name}
                onChange={(e) => handleParamChange(index, 'name', e.target.value)}
                placeholder="Parameter name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="text"
                value={param.value}
                onChange={(e) => handleParamChange(index, 'value', e.target.value)}
                placeholder="Value"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
              {index > 0 && (
                <button 
                  onClick={() => handleRemoveParam(index)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          
          <button 
            onClick={handleAddParam}
            className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Parameter
          </button>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-2">Example Custom Parameters</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
            <li><code className="bg-gray-100 px-1 py-0.5 rounded">userid</code> - Track specific users</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded">campaign</code> - Track marketing campaigns</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded">source</code> - Track traffic sources</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded">plan</code> - Track user subscription plans</li>
          </ul>
        </div>
      </div>
      
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Event Tracking</h2>
        <p className="text-gray-600 mb-4">
          Track specific user interactions using our JavaScript API:
        </p>
        
        <div className="relative bg-gray-50 p-4 rounded-md border border-gray-200 mb-6">
          <pre className="text-sm text-gray-800 overflow-x-auto">
{`// Track a custom event
VisitorStatus.trackEvent('category', 'action', 'label', value);

// Example: Track button clicks
document.querySelector('#signup-button').addEventListener('click', function() {
  VisitorStatus.trackEvent('Signup', 'ButtonClick', 'Homepage', 1);
});

// Example: Track form submissions
document.querySelector('#contact-form').addEventListener('submit', function() {
  VisitorStatus.trackEvent('Contact', 'FormSubmit', 'ContactPage', 1);
});`}
          </pre>
          <button 
            onClick={() => {
              navigator.clipboard.writeText(document.querySelector('pre').innerText);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }} 
            className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700"
            title="Copy to clipboard"
          >
            {copied ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
          </button>
        </div>
        
        <h3 className="text-md font-medium text-gray-900 mb-2">Common Event Categories</h3>
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
            <li><strong>Engagement</strong> - User interactions like button clicks, video plays, etc.</li>
            <li><strong>Ecommerce</strong> - Product views, add to cart, checkouts, purchases</li>
            <li><strong>Form</strong> - Form submissions, field completions, validation errors</li>
            <li><strong>Navigation</strong> - Menu clicks, page scrolls, tab changes</li>
            <li><strong>Content</strong> - Article reads, downloads, shares</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TrackingCodeManager;